import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { applyActionCode } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { MoonStar, CheckCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export default function AuthActionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const processedCode = useRef<string | null>(null);

  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');

    // If we have a code and it's different from the one we processed (or we haven't processed one yet)
    if (oobCode && oobCode !== processedCode.current) {
      processedCode.current = oobCode;

      // Reset state for new attempt if component is reused
      setIsProcessing(true);
      setError(null);
      setSuccess(null);

      const processAction = async () => {

        try {
          // Sign out first to prevent auth state listener interference on iOS
          // This prevents onAuthStateChanged from triggering during auth action processing
          if (auth.currentUser) {
            await auth.signOut();
          }

          // Extract action parameters from Firebase Auth action URL
          // mode and oobCode are already extracted above

          if (!mode || !oobCode) {
            setError("Invalid action link. Missing required parameters.");
            setIsProcessing(false);
            return;
          }

          // Handle different action modes
          switch (mode) {
            case 'resetPassword':
              // Redirect to password reset confirmation with the action code
              navigate(`/reset-password-confirm?oobCode=${oobCode}`, { replace: true });
              break;

            case 'verifyEmail':
              // Handle email verification if needed
              navigate('/login?message=email-verified', { replace: true });
              break;

            case 'recoverEmail':
              // Handle email recovery if needed
              navigate('/login?message=email-recovered', { replace: true });
              break;

            case 'verifyAndChangeEmail':
              // Handle email update verification
              try {
                await applyActionCode(auth, oobCode);

                // Try to reload user to get updated email, but handle token expiration
                try {
                  if (auth.currentUser) {
                    await auth.currentUser.reload();
                  }
                  navigate('/settings?message=email-updated', { replace: true });
                } catch (reloadErr: any) {
                  console.warn('Error reloading user after email update:', reloadErr);
                  // If token expired (common after email change), show success message instead of error
                  if (reloadErr.code === 'auth/user-token-expired' || reloadErr.code === 'auth/user-not-found') {
                    setSuccess("Email successfully updated. Please log in again to continue.");
                    setIsProcessing(false);
                  } else {
                    // For other errors, we still consider the email update successful but maybe show a warning
                    // or just redirect to settings anyway since the critical part (applyActionCode) succeeded
                    navigate('/settings?message=email-updated', { replace: true });
                  }
                }
              } catch (err) {
                console.error('Error verifying email change:', err);
                setError("Failed to verify email change. The link may have expired or already been used.");
                setIsProcessing(false);
              }
              break;

            default:
              setError(`Unsupported action mode: ${mode}`);
              setIsProcessing(false);
          }
        } catch (err) {
          console.error('Error processing auth action:', err);
          setError("An error occurred while processing the action link.");
          setIsProcessing(false);
        }
      };

      processAction();
    } else if (!oobCode && !processedCode.current) {
      // No code provided and we haven't processed anything
      setError("Invalid action link. Missing required parameters.");
      setIsProcessing(false);
    }
  }, [searchParams, navigate]);

  if (isProcessing) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center gap-4 text-center mb-8">
            <MoonStar className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-bold text-foreground">
              Processing...
            </h1>
            <p className="text-muted-foreground">
              Please wait while we process your request.
            </p>
          </div>
          <Card className="w-full max-w-md mx-auto">
            <CardContent className="flex items-center justify-center p-6">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center gap-4 text-center mb-8">
            <MoonStar className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-bold text-foreground">
              Success
            </h1>
            <p className="text-muted-foreground">
              Your request has been processed successfully.
            </p>
          </div>
          <Card className="w-full max-w-md mx-auto">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
              <CardTitle className="text-center">Email Verified</CardTitle>
              <CardDescription className="text-center">
                {success}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/login', { replace: true });
                }}
                className="w-full"
              >
                Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center gap-4 text-center mb-8">
            <MoonStar className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-bold text-foreground">
              Action Failed
            </h1>
            <p className="text-muted-foreground">
              We couldn't process your request.
            </p>
          </div>
          <Card className="w-full max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="text-center">Error</CardTitle>
              <CardDescription className="text-center">
                {error}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Alert variant="destructive">
                  <AlertDescription>
                    Please try requesting a new link or contact support if the problem persists.
                  </AlertDescription>
                </Alert>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/login', { replace: true });
                  }}
                  className="w-full"
                >
                  Back to Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return null;
}
